<template>
<div>
    <q-toolbar color="white" text-color="grey-9" class="top-page-toolbar">
        <q-toolbar-title class="q-body-2">
            <span class="text-grey-10" >
                {{ $tc('finance', 2) }}
            </span> / <span class="text-weight-bold">{{ $t('overview') }}</span>
        </q-toolbar-title>
        <div style="display: flex; position: absolute; right: 10px;">
            <div style="display: flex; align-items: center;">
                <q-select
                v-model="selectedYear"
                :placeholder="$t('finances_year')"
                :options="averageYearOptions"
                class="q-mr-sm"
                no-parent-field
                hide-underline
                />
            </div>
            <div style=" display: flex; align-items: center;">
                <q-select
                v-model="selectedTimeSpan"
                :placeholder="$t('finances_time_span')"
                :options="timeSpanOptions"
                class="q-mr-sm"
                no-parent-field
                hide-underline
                />
            </div>
        </div>
    </q-toolbar>
    <div class="layout-padding">
        <div class="full-width" style="display: flex; flex-flow: row wrap; justify-content: space-evenly; align-content: space-between;">
            <selected-time-card
              v-for="(data, index) in dataArray"
              :key="index"
              :time="data.time"
              :year="data.year"
              :index="index"
              @cardClick="handleCardClick"
            />
        </div>
        <finances-display
          :revenue="currentRevenue"
          :expense="currentExpense"
          :time="currentTimeSpan"
        />
    </div>
</div>
</template>

<script>
  import FinancesDisplay from 'components/Finances/FinancesDisplay.vue'
  import SelectedTimeCard from 'components/Finances/SelectedTimeCard.vue'
  import { StatisticsClient } from 'src/api/Statistics'
  import { formatMoney } from 'src/utils/format'
  import { general } from 'src/config'
  import { mapGetters } from 'vuex'
  import parseDate from 'date-fns/parse'

  export default {
    data () {
      return {
        timeSpanOptions: [
          {
            label: this.$t('finances_yearly'),
            value: 'yearly'
          },
          {
            label: this.$t('finances_quaterly'),
            value: 'quarterly'
          },
          {
            label: this.$t('finances_monthly'),
            value: 'monthly'
          }
        ],
        selectedYear: new Date().getFullYear(),
        selectedTimeSpan: 'yearly',
        revenues: [],
        expenses: [],
        currentTimeSpan: new Date().getFullYear(),
        currentRevenue: 0,
        currentExpense: 0,
        isLoading: true
      }
    },
    computed: {
      ...mapGetters(['activeTeam']),
      averageYearOptions () {
        const currentYear = new Date().getFullYear()
        const teamCreatedAt = this.activeTeam ? parseDate(this.activeTeam.created_at.date, this.$t('date_time_format')).getFullYear() : currentYear

        const values = []

        for (let i = teamCreatedAt; i <= currentYear; i++) {
          values.push({ label: i.toString(), value: i })
        }

        return values
      },
      currency () {
        return general.base_currency
      },
      startDate () {
        return new Date(`${this.selectedYear}-01-01`)
      },
      endDate () {
        const currentDate = new Date()
        const currentYear = currentDate.getFullYear()
        if (this.selectedYear === currentYear) {
          return new Date()
        } else {
          return new Date(`${this.selectedYear}-12-31`)
        }
      },
      chunkSize () {
        if (this.selectedTimeSpan === 'monthly') {
          return 1
        } else if (this.selectedTimeSpan === 'yearly') {
          return 12
        } else return 3
      },
      dataArray () {
        if (this.selectedTimeSpan === 'yearly' && this.isLoading === false) {
          const totalExpenses = this.expenses[0].value
          const totalRevenue = this.revenues[0].value

          return [
            {
              time: this.$t('finances_full_year').toUpperCase(),
              fullTime: this.selectedYear,
              year: this.selectedYear,
              revenue: totalRevenue,
              expense: totalExpenses
            }
          ]
        } else if (this.selectedTimeSpan === 'monthly' && this.isLoading === false) {
          const months = [
            {
              short: this.$t('finances_month_abbr_january').toUpperCase(),
              long: this.$t('finances_month_january')
            }, {
              short: this.$t('finances_month_abbr_february').toUpperCase(),
              long: this.$t('finances_month_february')
            }, {
              short: this.$t('finances_month_abbr_march').toUpperCase(),
              long: this.$t('finances_month_march')
            }, {
              short: this.$t('finances_month_abbr_april').toUpperCase(),
              long: this.$t('finances_month_april')
            }, {
              short: this.$t('finances_month_abbr_may').toUpperCase(),
              long: this.$t('finances_month_may')
            }, {
              short: this.$t('finances_month_abbr_june').toUpperCase(),
              long: this.$t('finances_month_june')
            }, {
              short: this.$t('finances_month_abbr_july').toUpperCase(),
              long: this.$t('finances_month_july')
            }, {
              short: this.$t('finances_month_abbr_august').toUpperCase(),
              long: this.$t('finances_month_august')
            }, {
              short: this.$t('finances_month_abbr_september').toUpperCase(),
              long: this.$t('finances_month_september')
            }, {
              short: this.$t('finances_month_abbr_october').toUpperCase(),
              long: this.$t('finances_month_october')
            }, {
              short: this.$t('finances_month_abbr_november').toUpperCase(),
              long: this.$t('finances_month_november')
            }, {
              short: this.$t('finances_month_abbr_december').toUpperCase(),
              long: this.$t('finances_month_december')
          }]

          const data = this.revenues.map((rev, index) => {
            return {
              time: months[index].short,
              fullTime: months[index].long,
              year: this.selectedYear,
              revenue: rev.value,
              expense: this.expenses[index].value
            }
          })

          return data
        } else if (this.selectedTimeSpan === 'quarterly' && this.isLoading === false) {
          const quarters = [
            {
              short: this.$t('finances_quarters_abbr_jan_mar').toUpperCase(),
              long: this.$t('finances_quarters_jan_mar')
            }, {
              short: this.$t('finances_quarters_abbr_apr_jun').toUpperCase(),
              long: this.$t('finances_quarters_apr_jun')
            }, {
              short: this.$t('finances_quarters_abbr_jul_sep').toUpperCase(),
              long: this.$t('finances_quarters_jul_sep')
            }, {
              short: this.$t('finances_quarters_abbr_oct_dec').toUpperCase(),
              long: this.$t('finances_quarters_oct_dec')
          }]
          const data = this.revenues.map((revenue, index) => {
            return {
              time: quarters[index].short,
              fullTime: quarters[index].long,
              year: this.selectedYear,
              revenue: revenue.value,
              expense: this.expenses[index].value
            }
          })
          return data
        }
      }
    },
    methods: {
      fetchData () {
        const payload = {
          'statistics': [
            { 'name': 'revenue', 'options': { 'unit': 'month', 'start_date': this.startDate, 'end_date': this.endDate, 'chunk_size': this.chunkSize } },
            { 'name': 'expenses', 'options': { 'unit': 'month', 'start_date': this.startDate, 'end_date': this.endDate, 'chunk_size': this.chunkSize } }
          ]
        }

        const statisticsClient = new StatisticsClient()

        statisticsClient
          .list(payload)
          .then(response => {
            this.expenses = [...response.data.data[1].value]
            this.revenues = [...response.data.data[0].value]
            this.isLoading = false
          })
          .catch(err => console.error(err))
      },
      handleCardClick (index) {
        this.currentTimeSpan = this.dataArray[index].fullTime
        this.currentRevenue = formatMoney(this.dataArray[index].revenue, this.currency, 0, 2)
        this.currentExpense = formatMoney(this.dataArray[index].expense, this.currency, 0, 2)
      }
    },
    watch: {
      selectedTimeSpan: function () {
        this.isLoading = true
        this.fetchData()
      },
      selectedYear: function () {
        this.isLoading = true
        this.fetchData()
      },
      isLoading: function () {
        if (!this.isLoading) {
          this.currentTimeSpan = this.dataArray[0].fullTime
          this.currentRevenue = formatMoney(this.dataArray[0].revenue, this.currency, 0, 2)
          this.currentExpense = formatMoney(this.dataArray[0].expense, this.currency, 0, 2)
        }
      }
    },
    created () {
      this.fetchData()
    },
    components: {
      SelectedTimeCard,
      FinancesDisplay
    }
  }
</script>
